import { SelectOption } from 'lib/components/types';
import React, { useState } from 'react';
import Select from 'react-select';
import JobType from '../../lib/util/JobType';
import classes from './index.module.scss';

/**
 * Props for the PrioritySelect component
 */
interface Props {
	value?: JobType; // Currently selected job priority type
	defaultValue?: SelectOption<JobType>; // Default value for the select component
	setValue: React.Dispatch<React.SetStateAction<JobType>>; // Callback to update selected value
}

/**
 * Dropdown component for selecting job priority (Routine or STAT)
 * Used in job creation and editing forms
 *
 * @param value - Currently selected job priority type
 * @param setValue - Callback to update selected value
 * @param defaultValue - Default value for the select component
 */
const PrioritySelect = ({ value, setValue, defaultValue }: Props) => {
	/**
	 * Available options for job priority
	 * Transforms the JobType enum values into the format required by react-select
	 */
	const options: SelectOption<number>[] = [
		{
			id: JobType.ROUTINE,
			name: 'Routine',
		},
		{
			id: JobType.STAT,
			name: 'STAT',
		},
	].map(({ id, name }) => ({
		value: id,
		label: name,
	}));

	/**
	 * Local state for the selected option
	 * Initialized from the value prop if provided
	 */
	const [option, setOption] = useState<SelectOption<JobType> | null>(
		typeof value !== undefined ? options.find((option) => option.value === value)! : null
	);

	/**
	 * Handle selection change in the dropdown
	 * Updates both local state and parent component state
	 *
	 * @param option - The newly selected option or null if cleared
	 */
	const handleChange = (option: SelectOption<JobType> | null) => {
		setOption(option);
		setValue(option!.value as JobType);
	};

	/**
	 * Render the react-select dropdown component
	 * Configured with custom styling and behavior options
	 */
	return (
		<Select
			placeholder=""
			defaultValue={defaultValue}
			className={classes['react-select-container']}
			classNamePrefix="react-select"
			options={options}
			isClearable={true} // Allow clearing the selection
			isSearchable={true} // Allow searching by typing
			value={option}
			onChange={handleChange}
		/>
	);
};

export default PrioritySelect;
